import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { fieldErrors, toApiErrorMessage } from '@/lib/api-client';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { authApi } from './auth-api';
import { AuthLayout } from './auth-layout';
import { resetPasswordFormSchema, type ResetPasswordFormValues } from './auth-schemas';
import { usePasswordRulesHint } from './use-password-rules';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [formError, setFormError] = useState<string | null>(null);
  const passwordHint = usePasswordRulesHint();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordFormSchema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    if (!token) return;
    setFormError(null);

    try {
      await authApi.resetPassword({ token, ...values });
      toast.success('Password reset. Please sign in.');
      void navigate('/login', { replace: true });
    } catch (error) {
      // The server owns password complexity (it is runtime-configurable), so
      // its per-rule messages are surfaced verbatim on the field.
      const fields = fieldErrors(error);
      const passwordIssues = fields.password ?? fields.newPassword;

      if (passwordIssues?.length) {
        setError('newPassword', { message: passwordIssues.join('. ') });
      } else {
        setFormError(toApiErrorMessage(error));
      }
    }
  });

  if (!token) {
    return (
      <AuthLayout
        title="Invalid link"
        footer={
          <Link to="/forgot-password" className="text-muted-foreground hover:text-foreground">
            Request a new link
          </Link>
        }
      >
        <Alert variant="destructive">
          <AlertCircle aria-hidden="true" />
          <AlertDescription>
            This reset link is missing its token. Request a new one.
          </AlertDescription>
        </Alert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Set a new password" description="Choose a password you have not used before.">
      <form onSubmit={(event) => void onSubmit(event)} className="space-y-4" noValidate>
        {formError && (
          <Alert variant="destructive">
            <AlertCircle aria-hidden="true" />
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}

        <FormField
          label="New password"
          error={errors.newPassword?.message}
          hint={passwordHint}
          required
        >
          {(props) => (
            <Input
              {...props}
              {...register('newPassword')}
              type="password"
              autoComplete="new-password"
              autoFocus
            />
          )}
        </FormField>

        <FormField label="Confirm new password" error={errors.confirmPassword?.message} required>
          {(props) => (
            <Input
              {...props}
              {...register('confirmPassword')}
              type="password"
              autoComplete="new-password"
            />
          )}
        </FormField>

        <Button type="submit" className="w-full" loading={isSubmitting}>
          Reset password
        </Button>
      </form>
    </AuthLayout>
  );
}
